import React from "react";
import { Link, useLocation } from "react-router-dom";
import { EnvelopeSimple, ShieldCheck, Clock, QrCode } from "@phosphor-icons/react";
import Seo from "../components/Seo";

const Step = ({ icon: Icon, title, body, accent }) => (
  <div className="hard-border bg-cyber-surface p-5 flex gap-4">
    <div className={`shrink-0 w-10 h-10 border flex items-center justify-center ${accent ? 'border-cyber-green text-cyber-green' : 'border-cyber-line text-white/80'}`}>
      <Icon size={18} weight="duotone" />
    </div>
    <div>
      <div className="font-display font-black text-lg text-white leading-tight">{title}</div>
      <p className="text-white/60 text-sm mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);

const RsvpSuccess = () => {
  const { state } = useLocation();
  const email = state?.email || "your inbox";
  const eventTitle = state?.eventTitle;

  return (
    <main className="grain min-h-screen flex items-center" data-testid="rsvp-success-page">
      <Seo title="Invitation requested" description="Your FintechOmni invitation request was received. Check your inbox for the confirmation." noindex />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 w-full">
        <div className="hard-border bg-cyber-surface p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 bg-cyber-green animate-blink" />
              <span className="term-label">/ invitation requested · check your email</span>
            </div>

            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl mt-6 leading-[0.95]">
              Thanks.<br />
              <span className="text-white">Now check your inbox.</span>
            </h1>

            <p className="text-white/75 mt-6 text-lg max-w-2xl leading-relaxed">
              {eventTitle ? (<>We've just emailed a confirmation to <span className="font-mono-tech text-cyber-green break-all">{email}</span> with everything about your invitation request for <strong className="text-white">{eventTitle}</strong> and what happens next.</>) : (<>We've just emailed a confirmation to <span className="font-mono-tech text-cyber-green break-all">{email}</span> with everything about your invitation request and what happens next.</>)}
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Step
                icon={EnvelopeSimple}
                title="1. Confirmation email"
                body="In your inbox now. It confirms we've got your request and summarises what happens next."
                accent
              />
              <Step
                icon={ShieldCheck}
                title="2. Personal review"
                body="A human on our team reviews every request — never an algorithm — so the room stays genuinely useful for everyone."
              />
              <Step
                icon={QrCode}
                title="3. Your QR code"
                body="If your invitation is approved, you'll receive a second email with your personal QR code, final venue details, and door instructions."
              />
            </div>

            <div className="mt-10 hard-border p-5 bg-cyber-black flex items-start gap-4">
              <Clock size={20} weight="duotone" className="text-cyber-green shrink-0 mt-0.5" />
              <div>
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-cyber-green">/ why we do this</div>
                <p className="text-white/80 mt-2 text-sm leading-relaxed">
                  We hand-review every invitation request because the room is the product. Curating for genuinely relevant attendees — fintech marketing operators, not tourists — is what makes these events worth your time. If you do not receive a confirmation email, your invitation request was not approved for this event.
                </p>
              </div>
            </div>

            <p className="text-white/40 font-mono-tech text-xs mt-6">/ can't find the email? Check your spam folder or filters — it's from <span className="text-white/70">app@fintechomni.com</span>.</p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/" className="btn-neon" data-testid="success-home-cta">Back to home</Link>
              <Link to="/events" className="btn-ghost-neon" data-testid="success-events-cta">Browse other events</Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RsvpSuccess;
